import { useCloudApi } from '@/api/cloudApi'
import {
  createInstance,
  deleteInstance,
  fetchInstances,
  updateInstanceAnnotations
} from '@/api/instances'
import { useAnalytics } from '@/composables/useAnalytics'
import { i18n } from '@/lang'
import type {
  V1alpha1PoolOption,
  V1alpha1PulsarInstance,
  V1alpha1Instance
} from '@streamnative/cloud-api-client-typescript'
import type { Workspace } from '@/composables/useWorkspace'
import type { CloudV1alpha1PoolRef } from '@streamnative/cloud-api-client-typescript'
import type { PulsarState } from './usePulsarState'
import { capitalize } from 'lodash-es'
import dayjs from 'dayjs'
import { usePoolsV2 } from './usePoolsV2'
import type { DeploymentType } from '@/api/organizations'

const engineAnnotation = 'cloud.streamnative.io/engine'

// Union type for both instance types
export type InstanceGroup = V1alpha1PulsarInstance | V1alpha1Instance

// Type guard to check if instance is V1alpha1PulsarInstance by finalizer
const PULSAR_INSTANCE_FINALIZER = 'pulsarinstance.finalizers.cloud.streamnative.io'
export function isPulsarInstance(instance: InstanceGroup): instance is V1alpha1PulsarInstance {
  return Array.isArray(instance?.metadata?.finalizers)
    ? instance.metadata.finalizers.includes(PULSAR_INSTANCE_FINALIZER)
    : false
}

export function isPulsarInstanceByName(name: string): boolean {
  const instance = getInstanceByName(name)
  if (!instance) {
    return false
  }
  return isPulsarInstance(instance)
}

let lastOrg: string | undefined = undefined
const { t } = i18n.global
const api = useCloudApi()
const { findPool } = usePoolsV2()
const instances = ref<Array<InstanceGroup>>([])
const poolOptions = ref<Array<V1alpha1PoolOption>>([]) // don't use this, usePool() instead
const activeInstance = computed(() => {
  const { instance } = usePulsarState()
  if (instance.value === undefined || instance.value === '') {
    return undefined
  }

  const foundInstance = instances.value.find(i => i.metadata?.name === instance.value)

  if (foundInstance) {
    return foundInstance
  }
  // TODO need to add this back in when adding in last viewed org/ins
  // if (!foundInstance && instance.value) {
  //   // instance is set but not found
  //   clearLastViewedOrgIns()
  // }
  return undefined
})
const instanceLocations = computed(() => {
  return instanceLocationsFunc(activeInstance.value)
})

const instanceLocationsFunc = (instanceObj?: InstanceGroup) => {
  const pool = findPool(instanceObj?.spec?.poolRef)
  return pool?.spec?.locations ?? []
}

const activeInstanceType = computed(() => {
  return activeInstanceTypeFunc(activeInstance.value)
})

const activeInstanceTypeFunc = (ins?: InstanceGroup) => {
  return ins?.spec?.type
}

const activeInstanceEngineFunc = (ins?: InstanceGroup) => {
  if (ins?.metadata?.annotations?.[engineAnnotation] === 'ursa') {
    return 'ursa'
  }
  return 'classic'
}

const managedOrHosted = computed(() => {
  return getManagedOrHosted(activeInstance.value)
})

const managedOrHostedLabel = computed(() => {
  return getManagedOrHostedLabel(activeInstance.value)
})

// don't use this.  Use getInstanceDeploymentType.  There are existing serverless logic relying on poolref
// not being defined and inferring serverless type.  Otherwise I would have gotten rid of this
const getManagedOrHosted = (instance?: InstanceGroup): 'hosted' | 'managed' | undefined => {
  if (instance?.spec?.poolRef) {
    return instance.spec.poolRef.namespace === 'streamnative' ? 'hosted' : 'managed'
  }
  return undefined
}

const getManagedOrHostedLabel = (instance?: InstanceGroup) => {
  return capitalize(getManagedOrHosted(instance))
}

const getInstanceDeploymentType = (
  instance?: InstanceGroup
): 'hosted' | 'managed' | 'managed-pro' | 'hosted-pro' | 'serverless' | undefined => {
  if (!instance) {
    return undefined
  }

  if (instance.spec?.type === 'serverless') {
    return 'serverless'
  }

  const pool = findPool(instance?.spec?.poolRef)
  if (!pool) {
    return instance.spec?.poolRef?.namespace === 'streamnative' ? 'hosted' : 'managed'
  }
  return pool.deploymentType as Exclude<DeploymentType, 'serverless'>
}

const getInstanceDeploymentLabel = (instance?: InstanceGroup) => {
  const deploymentType = getInstanceDeploymentType(instance)
  if (!deploymentType) {
    return undefined
  }

  switch (deploymentType) {
    case 'serverless':
      return 'Serverless'
    case 'managed':
      return 'BYOC'
    case 'managed-pro':
      return 'BYOC-PRO'
    case 'hosted':
      return 'Dedicated'
    case 'hosted-pro':
      return 'Dedicated-PRO'
  }
}

const instanceTypeLabels: Record<string, string> = {
  serverless: 'Serverless',
  dedicated: 'Dedicated',
  'dedicated-pro': 'Dedicated Pro',
  byoc: 'BYOC',
  'byoc-pro': 'BYOC Pro'
}

const getInstanceTypeLabel = (instance?: InstanceGroup): string | undefined => {
  const type = instance?.spec?.type
  if (!type) return undefined
  return instanceTypeLabels[type] ?? type
}

const activeInstanceDeploymentType = computed(() => {
  return getInstanceDeploymentType(activeInstance.value)
})

const instanceScopes = ref<string>()

export const INSTANCE_DETAILS: Record<string, Record<string, Record<string, string>>> = {
  regions: {
    GCP: {
      value: 'GCP',
      label: 'Google Cloud',
      cloudProvider: 'gcloud',
      icon: 'iconfont icon-google_cloud-icon'
    },
    AWS: {
      value: 'AWS',
      label: 'AWS',
      cloudProvider: 'aws',
      icon: 'iconfont icon-aws'
    },
    AZURE: {
      value: 'AZURE',
      label: 'AZURE',
      cloudProvider: 'azure',
      icon: 'iconfont icon-azure'
    },
    ALICLOUD: {
      value: 'ALICLOUD',
      label: 'Alibaba Cloud',
      cloudProvider: 'alicloud',
      icon: 'iconfont icon-alibaba-cloud'
    }
  },
  plans: {
    FREE: {
      name: 'instance.free',
      value: 'FREE'
    },
    STANDARD: {
      name: 'instance.standard',
      value: 'STANDARD'
    },
    SERVERLESS: {
      name: 'instance.serverless',
      value: 'SERVERLESS'
    },
    DEDICATED: {
      name: 'instance.dedicated',
      value: 'DEDICATED'
    },
    BYOC: {
      name: 'instance.byoc',
      value: 'BYOC'
    },
    BYOCPRO: {
      name: 'instance.byocPro',
      value: 'BYOC-PRO'
    }
  },
  zonals: {
    zonal: {
      value: 'zonal',
      label: 'instance.saz',
      azValue: 'SAZ'
    },
    regional: {
      value: 'regional',
      label: 'instance.maz',
      azValue: 'MAZ'
    }
  }
} as const

const timeOptions = ref([
  '12:00 AM',
  '12:15 AM',
  '12:30 AM',
  '12:45 AM',
  '1:00 AM',
  '1:15 AM',
  '1:30 AM',
  '1:45 AM',
  '2:00 AM',
  '2:15 AM',
  '2:30 AM',
  '2:45 AM',
  '3:00 AM',
  '3:15 AM',
  '3:30 AM',
  '3:45 AM',
  '4:00 AM',
  '4:15 AM',
  '4:30 AM',
  '4:45 AM',
  '5:00 AM',
  '5:15 AM',
  '5:30 AM',
  '5:45 AM',
  '6:00 AM',
  '6:15 AM',
  '6:30 AM',
  '6:45 AM',
  '7:00 AM',
  '7:15 AM',
  '7:30 AM',
  '7:45 AM',
  '8:00 AM',
  '8:15 AM',
  '8:30 AM',
  '8:45 AM',
  '9:00 AM',
  '9:15 AM',
  '9:30 AM',
  '9:45 AM',
  '10:00 AM',
  '10:15 AM',
  '10:30 AM',
  '10:45 AM',
  '11:00 AM',
  '11:15 AM',
  '11:30 AM',
  '11:45 AM',
  '12:00 PM',
  '12:15 PM',
  '12:30 PM',
  '12:45 PM',
  '1:00 PM',
  '1:15 PM',
  '1:30 PM',
  '1:45 PM',
  '2:00 PM',
  '2:15 PM',
  '2:30 PM',
  '2:45 PM',
  '3:00 PM',
  '3:15 PM',
  '3:30 PM',
  '3:45 PM',
  '4:00 PM',
  '4:15 PM',
  '4:30 PM',
  '4:45 PM',
  '5:00 PM',
  '5:15 PM',
  '5:30 PM',
  '5:45 PM',
  '6:00 PM',
  '6:15 PM',
  '6:30 PM',
  '6:45 PM',
  '7:00 PM',
  '7:15 PM',
  '7:30 PM',
  '7:45 PM',
  '8:00 PM',
  '8:15 PM',
  '8:30 PM',
  '8:45 PM',
  '9:00 PM',
  '9:15 PM',
  '9:30 PM',
  '9:45 PM',
  '10:00 PM',
  '10:15 PM',
  '10:30 PM',
  '10:45 PM',
  '11:00 PM',
  '11:15 PM',
  '11:30 PM',
  '11:45 PM'
])

const durationOptions = ref(['4h', '6h', '8h', '12h', '24h'])

const setInstances = (_instances: Array<InstanceGroup>) => {
  const byName = new Map<string, InstanceGroup>()
  for (const inst of _instances) {
    const name = inst.metadata?.name
    if (name) {
      byName.set(name, inst)
    }
  }
  const deduped = Array.from(byName.values())
  instances.value = deduped.sort((a, b) =>
    (a.metadata?.name ?? '').localeCompare(b.metadata?.name ?? '')
  )
}

const getPoolOptions = async ({ organization }: { organization: string }) => {
  const options = await api.listNamespacedPoolOption(organization)
  poolOptions.value = options.data.items
}

export const removeInstance = async (organization: string, instance: string) => {
  const data = await deleteInstance({
    organization,
    instanceName: instance,
    instanceType: isPulsarInstanceByName(instance) ? 'pulsar' : 'kafka'
  })

  useAnalytics().identifyUser()

  if (!data || data.status === 'Failure') {
    throw Error(t('instance.errorDelete'))
  }
  await getInstances({ organization })
  const _instance = instances.value.find(i => i?.metadata?.name !== instance)
  if (_instance && _instance.metadata && !_instance.metadata.deletionTimestamp) {
    _instance.metadata.deletionTimestamp = dayjs().format('YYYY-MM-DDTHH:mm:ssZ')
  }
}

export const addInstance = async (payload: {
  instance: string
  availabilityMode: 'zonal' | 'regional'
  type: 'FREE' | 'STANDARD' | 'SERVERLESS' | 'DEDICATED' | 'BYOC' | 'BYOC-PRO' | undefined
  poolRef?: CloudV1alpha1PoolRef
  organization: string
}) => {
  if (!payload.instance) {
    throw t('instance.inputInstanceNameNotification')
  }
  if (!payload.poolRef) {
    throw t('instance.inputPoolNotification')
  }
  const newInstance = await createInstance(payload)

  useAnalytics().identifyUser()

  setInstances([...instances.value, newInstance])
}

/**
 * Update instance engine annotation
 * @param instanceName - Instance name
 * @param organization - Organization namespace
 * @param isURSAFeaturePossible - Whether URSA feature is possible for this instance
 * @param clusterProfile - Cluster profile ('cost-optimized' or 'latency-optimized')
 */
export const updateInstanceEngineAnnotation = async (payload: {
  instanceName: string
  organization: string
  clusterType: 'pulsar' | 'kafka'
  isURSAFeaturePossible: boolean
  clusterProfile: string
}) => {
  const engine =
    payload.isURSAFeaturePossible && payload.clusterProfile === 'cost-optimized'
      ? 'ursa'
      : 'classic'

  await updateInstanceAnnotations({
    instanceName: payload.instanceName,
    organization: payload.organization,
    clusterType: payload.clusterType,
    annotations: {
      'cloud.streamnative.io/engine': engine
    }
  })

  // Update local state
  const instanceIndex = instances.value.findIndex(i => i.metadata?.name === payload.instanceName)
  if (instanceIndex > -1) {
    const instance = instances.value[instanceIndex]
    if (instance.metadata) {
      instance.metadata.annotations = {
        ...instance.metadata.annotations,
        'cloud.streamnative.io/engine': engine
      }
    }
  }
}

export const isLoadingInstances = ref(false)
export const getInstances = async ({ organization }: { organization: string }) => {
  isLoadingInstances.value = true
  try {
    const res = await fetchInstances(organization)
    setInstances(res.items)
    // track instance count change
    useAnalytics().identifyUser()
  } catch (e) {
    useError('Failed to fetch instances')
  } finally {
    isLoadingInstances.value = false
  }
}

export const istioEnabled = computed(() => {
  const annotations = activeInstance.value?.metadata?.annotations
  return (
    !!annotations && annotations?.['annotations.cloud.streamnative.io/istio-enabled'] === 'true'
  )
})

export const isIstioEnabledForInstance = (instance?: InstanceGroup) => {
  const annotations = instance?.metadata?.annotations
  return (
    !!annotations && annotations?.['annotations.cloud.streamnative.io/istio-enabled'] === 'true'
  )
}

export const isPaidInstance = computed(() => {
  return activeInstance.value?.spec?.type !== 'free'
})

export const getTranslatedInstanceDetails = (instance?: InstanceGroup) => {
  if (!instance?.spec) {
    // It's possible that `translatedInstanceDetails()` getter is called before
    // activeInstance has been set.
    // Just return empty object here as it will get updated when instance is set.
    return {}
  }
  const type = instance.spec.type
  const poolRef = instance.spec.poolRef
  const availabilityMode =
    'availabilityMode' in instance.spec ? instance.spec.availabilityMode : undefined
  const name = instance.metadata?.name
  const { regions, plans, zonals } = INSTANCE_DETAILS

  if (!poolRef) {
    return {}
  }
  const pool = findPool(poolRef)
  let planName = undefined
  if (pool?.spec?.cloudType === 'gcloud') {
    planName = 'GCP'
  } else if (pool?.spec?.cloudType === 'aws') {
    planName = 'AWS'
  } else if (pool?.spec?.cloudType === 'azure') {
    planName = 'AZURE'
  } else if (pool?.spec?.cloudType === 'alicloud') {
    planName = 'ALICLOUD'
  }

  return {
    name,
    availabilityMode: availabilityMode ? t(zonals[availabilityMode].label) : undefined,
    // TODO none of these should be undefined, it causes render errors
    type: type ? plans[type.replace('-', '').toUpperCase()]?.name : undefined,
    plan: planName ? regions[planName].label : undefined,
    cloudProvider: planName ? regions[planName].cloudProvider : undefined,
    deploymentType: pool?.deploymentType,
    deploymentTypeLabel: pool?.deploymentTypeLabel
  }
}

export const translatedInstanceDetails = computed(() => {
  return getTranslatedInstanceDetails(activeInstance.value)
})

/**
 * Get audience for an instance based on its type and configuration
 * @param instance - The instance object (InstanceGroup)
 * @param organization - Organization name
 * @param instanceName - Instance name
 * @returns Audience string or undefined
 */
export const getInstanceAudience = (
  instance: InstanceGroup | undefined,
  organization: string | undefined,
  instanceName: string | undefined
): string | undefined => {
  if (!instance || !instance.status || !organization || !instanceName) {
    return undefined
  }
  // Only V1alpha1PulsarInstance has status.auth
  if (isPulsarInstance(instance) && 'auth' in instance.status) {
    return (
      (instance.status as any).auth?.oauth2?.audience ??
      `urn:sn:pulsar:${organization}:${instanceName}`
    )
  }
  // Only return cloud audience if auth annotation is v2
  const authAnnotation = instance.metadata?.annotations?.['cloud.streamnative.io/auth']
  if (authAnnotation === 'v2') {
    return `urn:sn:cloud:${organization}`
  } else {
    // Fallback to instance-specific audience for non-v2 instances
    return (
      (instance.status as any).auth?.oauth2?.audience ??
      `urn:sn:pulsar:${organization}:${instanceName}`
    )
  }
}

// TODO maybe replace?
export const audience = computed(() => {
  const { organization, instance } = usePulsarState()
  return getInstanceAudience(activeInstance.value, organization.value, instance.value)
})

export const isInstanceDeletedFn = (instance?: InstanceGroup) => {
  return !!instance?.metadata?.deletionTimestamp
}

export const isInstanceDeletedByNameFn = (name: string) => {
  const instance = instances.value.find(i => i.metadata?.name === name)
  if (!instance) {
    return false
  }
  return isInstanceDeletedFn(instance)
}

export const isInstanceReadyByNameFn = (name: string) => {
  const instance = instances.value.find(i => i.metadata?.name === name)
  if (!instance) {
    return false
  }
  return isInstanceReadyFn(instance)
}

export const isInstanceReadyFn = (instance?: InstanceGroup) => {
  const condition = instance?.status?.conditions?.find(c => c.type === 'Ready')
  return condition ? condition.status === 'True' : false
}

export const instanceReady = computed(() => {
  return isInstanceReadyFn(activeInstance.value)
})

export const instanceNames = computed(() => {
  return instances.value.map(instance => instance.metadata?.name ?? '')
})

export const hasInstances = computed(() => {
  return instances.value.length > 0
})

// locally scoped composable for pinging a cluster
// export const usePingInstance = (params: { organization?: string; instance: string }) => {
//   const { mustOrganization } = usePulsarState()
//   const instance = ref<V1alpha1PulsarInstance>()
//   const conditions = computed(() => instance.value?.status?.conditions ?? [])
//   const error = ref('')
//   const lock = new AsyncLock()
//   const { resume, pause, isActive } = useIntervalFn(async () => {
//     if (!lock.isBusy(params.instance)) {
//       await lock.acquire(params.instance, async () => {
//         try {
//           const { data } = await useCloudApi().readNamespacedPulsarInstance(
//             params.instance,
//             params.organization ?? mustOrganization()
//           )

//           instance.value = data
//         } catch (e) {
//           error.value = getErrorMessage(e)
//           pause()
//         }
//       })
//     }
//   }, 5000)
//
//   return {
//     instance,
//     conditions,
//     resume,
//     pause,
//     isActive,
//     error
//   }
// }

export const init = (initialState: PulsarState) => {
  const { organization } = usePulsarState()
  const { isRbacUpdating } = rbacHelper()
  const valueChanged = async ([org, ab]: [string | undefined, boolean | undefined]) => {
    if (!org) {
      instances.value = []
      poolOptions.value = []
      lastOrg = undefined
      return
    }
    if (ab) {
      return
    }

    const isOrgChanged = org !== lastOrg

    if (isOrgChanged) {
      const { canDescribeInstanceList, canDescribePoolOptionList } = rbacManager()
      if (canDescribeInstanceList()) {
        await getInstances({ organization: org })
      }
      if (canDescribePoolOptionList()) {
        await getPoolOptions({ organization: org })
      }
    }
    lastOrg = org
  }

  watch([organization, isRbacUpdating], valueChanged)
  return valueChanged([initialState.organization, isRbacUpdating.value])
}

// alias to match cluster computed of similar name
const isActiveInstanceReady = computed(() => {
  return instanceReady.value
})

const isActiveInstanceServerless = computed(() => {
  return activeInstance.value?.spec?.type === 'serverless'
})

const isActiveInstanceBYOC = computed(() => {
  const type = activeInstance.value?.spec?.type
  return type === 'byoc' || type === 'byoc-pro'
})

const isInstanceUrsaFn = (instance?: InstanceGroup) => {
  return instance?.metadata?.annotations?.[engineAnnotation] === 'ursa'
}

const isActiveInstanceUrsa = computed(() => {
  return isInstanceUrsaFn(activeInstance.value)
})

const getCloudProviderByName = (name: string) => {
  const instance = instances.value.find(i => i.metadata?.name === name)
  if (!instance) {
    return 'Unknown'
  }
  const pool = findPool(instance?.spec?.poolRef)
  if (pool?.spec?.cloudType === 'gcloud') {
    return 'Google Cloud'
  } else if (pool?.spec?.cloudType === 'aws') {
    return 'AWS'
  } else if (pool?.spec?.cloudType === 'azure') {
    return 'AZURE'
  }
  return 'Unknown'
}

const getInstanceStatusByName = (
  instance: string,
  clusters: any[],
  kafkaClusters: any[],
  workspaces: Workspace[] = []
) => {
  const _instance = instances.value.find(item => item.metadata?.name === instance)
  if (!_instance) {
    return 'unknown'
  }
  return getInstanceStatus(_instance, clusters, kafkaClusters, workspaces)
}

const getInstanceStatus = (
  instance: InstanceGroup,
  clusters: any[],
  kafkaClusters: any[],
  workspaces: Workspace[] = []
) => {
  const condition = instance.status?.conditions?.find(c => c.type === 'Ready')
  const isInstanceReady = condition ? condition.status === 'True' : false

  const noClusters = !clusters || clusters.length === 0
  const noKafkaClusters = !kafkaClusters || kafkaClusters.length === 0
  const noWorkspaces = !workspaces || workspaces.length === 0

  const isAllClusterAvailable =
    !clusters || clusters.length === 0 || !!clusters.every(c => c?.generated.conditions?.Available)
  const isAnyClusterDeleted =
    clusters && clusters.length > 0 && !!clusters.find(c => c.generated.deleted)

  const isAllKafkaClusterAvailable =
    !kafkaClusters ||
    kafkaClusters.length === 0 ||
    !!kafkaClusters.every(c => c?.generated.conditions?.Available)
  const isAnyKafkaClusterDeleted =
    kafkaClusters && kafkaClusters.length > 0 && !!kafkaClusters.find(c => c.generated.deleted)

  const isAllWorkspacesAvailable =
    !workspaces ||
    workspaces.length === 0 ||
    workspaces.every(ws => ws.generated.conditions.Available)
  const isAnyWorkspaceDeleted =
    workspaces && workspaces.length > 0 && workspaces.some(ws => ws.generated.deleted)

  if (!isInstanceReady) {
    return 'not ready'
  } else if (noClusters && noKafkaClusters && noWorkspaces) {
    return 'no resource'
  } else if (isAllClusterAvailable && isAllKafkaClusterAvailable && isAllWorkspacesAvailable) {
    return 'ready'
  } else if (isAnyClusterDeleted || isAnyKafkaClusterDeleted || isAnyWorkspaceDeleted) {
    return 'deleting'
  } else if (!isAllClusterAvailable || !isAllKafkaClusterAvailable || !isAllWorkspacesAvailable) {
    return 'deploying'
  } else {
    return 'no resource'
  }
}

const getStatusLabel = (
  name: string,
  clusters: any[],
  kafkaClusters: any[],
  workspaces: Workspace[] = []
) => {
  const status = getInstanceStatusByName(name, clusters, kafkaClusters, workspaces).toLowerCase()
  return capitalize(status === 'ready' ? 'active' : status)
}

const getStatusColor = (
  name: string,
  clusters: any[],
  kafkaClusters: any[],
  workspaces: Workspace[] = []
) => {
  const status = getInstanceStatusByName(name, clusters, kafkaClusters, workspaces).toLowerCase()
  if (status === 'ready') {
    return 'positive'
  } else if (status === 'deploying') {
    return 'informational'
  } else if (status === 'deleting') {
    return 'negativehigh'
  }
  return 'neutral'
}

const activeInstanceEngine = computed(() => {
  return activeInstanceEngineFunc(activeInstance.value)
})

const viewableInstances = computed<{ label: string; disabled: boolean }[]>(() => {
  const { canDescribeInstance } = rbacManager()
  const { clusterMap } = useCluster()
  const { kafkaClusterMap } = useKafkaCluster()
  const { workspaceMap } = useWorkspace()
  const instanceOptions = (instances.value || [])
    .map(i => {
      return {
        label: i?.metadata?.name || '',
        value: i?.metadata?.name || '',
        id: i?.metadata?.uid || '',
        disabled: !isInstanceReadyFn(i) || !canDescribeInstance({ instance: i.metadata?.name })
      }
    })
    .sort((a, b) => {
      const aHasResources =
        (clusterMap.value[a.label] ?? []).length > 0 ||
        (kafkaClusterMap.value[a.label] ?? []).length > 0 ||
        (workspaceMap.value[a.label] ?? []).length > 0
      const bHasResources =
        (clusterMap.value[b.label] ?? []).length > 0 ||
        (kafkaClusterMap.value[b.label] ?? []).length > 0 ||
        (workspaceMap.value[b.label] ?? []).length > 0

      // Sort instances with resources first
      if (aHasResources && !bHasResources) {
        return -1
      }
      if (!aHasResources && bHasResources) {
        return 1
      }
      return a.label.localeCompare(b.label)
    })
  return instanceOptions
})

const getAvailableInstanceForUnilink = computed<{ label: string; disabled: boolean }[]>(() => {
  const { canDescribeInstance } = rbacManager()
  const instanceOptions = (instances.value || [])
    .filter(i => {
      return i?.metadata?.annotations?.[engineAnnotation] === 'ursa'
    })
    .map(i => {
      return {
        label: i?.metadata?.name || '',
        value: i?.metadata?.name || '',
        disabled: !isInstanceReadyFn(i) || !canDescribeInstance({ instance: i.metadata?.name })
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
  return instanceOptions
})

const instanceType = computed(() => {
  if (activeInstance.value?.spec?.type === 'serverless') {
    return 'serverless'
  } else if (activeInstance.value?.spec?.type === 'dedicated') {
    return 'hosted'
  } else if (activeInstance.value?.spec?.type === 'dedicated-pro') {
    return 'hosted-pro'
  } else if (activeInstance.value?.spec?.type === 'byoc') {
    return 'managed'
  } else if (activeInstance.value?.spec?.type === 'byoc-pro') {
    return 'managed-pro'
  }
  return 'hosted'
})

const getInstanceByName = (name: string) => {
  return instances.value.find(i => i.metadata?.name === name)
}

export const useInstance = () => {
  return {
    isInstanceDeletedByNameFn,
    isInstanceDeletedFn,
    isInstanceReadyByNameFn,
    isInstanceReadyFn,
    isActiveInstanceReady,
    instanceClaims: instanceScopes,
    instances,
    instanceLocations,
    activeInstance,
    poolOptions,
    removeInstance,
    addInstance,
    updateInstanceEngineAnnotation,
    getInstances,
    istioEnabled,
    isPaidInstance,
    getTranslatedInstanceDetails,
    translatedInstanceDetails,
    audience,
    getInstanceAudience,
    instanceReady,
    instanceNames,
    hasInstances,
    setInstances,
    timeOptions,
    durationOptions,
    init,
    managedOrHosted,
    managedOrHostedLabel,
    getManagedOrHosted,
    getManagedOrHostedLabel,
    isActiveInstanceServerless,
    isActiveInstanceBYOC,
    getCloudProviderByName,
    getInstanceStatusByName,
    getInstanceStatus,
    getInstanceDeploymentType,
    getInstanceDeploymentLabel,
    getInstanceTypeLabel,
    activeInstanceDeploymentType,
    getStatusLabel,
    getStatusColor,
    activeInstanceType,
    activeInstanceTypeFunc,
    activeInstanceEngineFunc,
    isActiveInstanceUrsa,
    isInstanceUrsaFn,
    isLoadingInstances,
    activeInstanceEngine,
    viewableInstances,
    getAvailableInstanceForUnilink,
    instanceLocationsFunc,
    instanceType,
    isPulsarInstance,
    getInstanceByName,
    isPulsarInstanceByName
  }
}
